import { useCallback, useRef, useState } from "react";
import { ModuleInputType } from "@me-team/host/main/graphql/types";
import { AllModulesTagName } from "../../../utils/enums";
import AccessService from "@me-team/host/services/AccessService";

type Params = {
    companyId: number;
    branchId: number;
    updateModules: (companyId: number, updates: ModuleInputType[], branchId: number) => Promise<void>;
};

export const useEnableOnlineBookingWithDeps = ({ companyId, branchId, updateModules }: Params) => {
    const accessService = AccessService.getInstance();

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<Error | null>(null);
    const inFlightRef = useRef<Promise<void> | null>(null); // чтобы не выполнять параллельно

    const enableWithDependencies = useCallback(async () => {
        if (loading && inFlightRef.current) {
            return inFlightRef.current;
        }

        const task = (async () => {
            setError(null);
            setLoading(true);
            try {
                const onlineBookingModule = accessService.getModuleByName(AllModulesTagName.BOOKING);
                if (!onlineBookingModule) {
                    throw new Error("Online Booking module not found");
                }

                const requiredDeps: AllModulesTagName[] = [
                    AllModulesTagName.PERSONNEL,
                    AllModulesTagName.LOGBOOK,
                    AllModulesTagName.SERVICES,
                    AllModulesTagName.CLIENTS,
                    AllModulesTagName.SERVICE_PROVIDES,
                ];

                const areDepsActive = accessService.areModulesActive(requiredDeps);

                if (onlineBookingModule.active && areDepsActive) {
                    return;
                }

                const updates: ModuleInputType[] = [];

                if (!onlineBookingModule.active) {
                    updates.push({ id: onlineBookingModule.id, isActive: true });
                }

                for (const tag of requiredDeps) {
                    const mod = accessService.getModuleByName(tag);
                    if (mod && !mod.active) {
                        updates.push({ id: mod.id, isActive: true });
                    }
                }

                if (updates.length === 0) {
                    return;
                }

                await updateModules(companyId, updates, branchId);
            } finally {
                setLoading(false);
                inFlightRef.current = null;
            }
        })();

        inFlightRef.current = task;
        try {
            await task;
        } catch (e) {
            setError(e as Error);
            throw e;
        }
    }, [accessService, companyId, branchId, updateModules, loading]);

    const onlineBookingModule = accessService.getModuleByName(AllModulesTagName.BOOKING);

    return {
        enableWithDependencies,
        onlineBookingModule,
        loading,
        error,
    };
};
